import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios instance used across the app — always sends cookies
export const api = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
};

export function AuthProvider({ children }) {
    // null = checking, false = unauthed, object = authed
    const [user, setUser] = useState(null);
    const [organization, setOrganization] = useState(null);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setOrganization(data.organization);
            return data.user;
        } catch (e) {
            setUser(false);
            setOrganization(null);
            return null;
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        setOrganization(data.organization);
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setUser(data.user);
        setOrganization(data.organization);
        return data;
    };

    const acceptInvite = async (token, password) => {
        const { data } = await api.post("/auth/users/accept-invite", {
            token,
            password,
        });
        setUser(data.user);
        setOrganization(data.organization);
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        setUser(false);
        setOrganization(null);
    };

    const value = {
        user,
        organization,
        login,
        register,
        acceptInvite,
        logout,
        refresh: fetchMe,
        isAuthed: !!user && user !== false,
        isLoading: user === null,
    };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// role ranking — must mirror backend auth.ROLES
const ROLE_RANK = {
    Admin: 0,
    QAManager: 1,
    QAAuditor: 2,
    SystemOwner: 3,
    Observer: 4,
};
export const roleAtLeast = (userRole, minRole) =>
    (ROLE_RANK[userRole] ?? 99) <= (ROLE_RANK[minRole] ?? 99);

export function formatApiError(err, fallback = "Something went wrong") {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d))
        return d
            .map((e) =>
                e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
            )
            .join(" ");
    if (typeof d?.msg === "string") return d.msg;
    return String(d);
}
